import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Send, Download, Pencil, X, FileText, Trash2,
  CheckCircle2, MailOpen, Ban, ExternalLink, FileCheck, Coins,
  AlertTriangle, ScanLine, Check, Mail, Loader2, RotateCcw, ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";

// Iter-205/206 — Outgoing Purchase Orders (BC émis par BAIM).

const STATUS_META = {
  DRAFT:            { label: "Brouillon",     color: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  ISSUED:           { label: "Émis",          color: "bg-blue-100 text-blue-800 border-blue-200" },
  SENT:             { label: "Envoyé",        color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  RECEIVED:         { label: "Reçu (AR)",     color: "bg-violet-100 text-violet-800 border-violet-200" },
  INVOICE_RECEIVED: { label: "Facture reçue", color: "bg-amber-100 text-amber-800 border-amber-200" },
  PAID:             { label: "Payée",         color: "bg-teal-100 text-teal-800 border-teal-200" },
  CANCELLED:        { label: "Annulé",        color: "bg-rose-100 text-rose-700 border-rose-200 line-through" },
};

const blankLine = () => ({ description: "", category: "Logiciel / SaaS", unit: "U", quantity: 1, rate: "", vat_rate: 0.20 });

export default function OutgoingPOs() {
  useAuth();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [meta, setMeta] = useState({ categories: [], statuses: [], currencies: [] });
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [creating, setCreating] = useState(null);     // form
  const [sending, setSending] = useState(null);       // { po, to, subject, body }
  const [receivingInvoice, setReceivingInvoice] = useState(null);  // Iter-206 — { po, step, file, draft, staged, verif, form }
  const [paying, setPaying] = useState(null);         // Iter-206 — { po, payment_date, payment_method, payment_reference, notes }
  // Iter-206y — new state for the 3 features.
  const [markingSent, setMarkingSent] = useState(null);  // { po, channel, recipient, note, submitting }
  const [missingEmail, setMissingEmail] = useState(null);  // { po, supplier_id, supplier_name, email, save_on_supplier, submitting }
  const [loading, setLoading] = useState(false);
  // Iter-228 — OCR scan of a supplier devis/facture to pre-fill the "Nouveau BC" form.
  const [scanning, setScanning] = useState(false);                      // OCR call in flight
  const [scanResult, setScanResult] = useState(null);                   // { document, supplier_match, lines, totals, ocr_error, confidence }
  const [scanInputKey, setScanInputKey] = useState(0);                  // bump to reset <input type="file" />

  const load = async () => {
    setLoading(true);
    try {
      const [pos, sup, m] = await Promise.all([
        api.get("/outgoing-pos"),
        api.get("/suppliers"),
        api.get("/outgoing-pos-meta/categories"),
      ]);
      setItems(pos.data || []);
      setSuppliers(sup.data || []);
      setMeta(m.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les BC");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Iter-214 — sortable columns. `sortBy` is null = default order (creation
  // desc as returned by API). When set, we apply a stable sort on the chosen
  // field with `sortDir` direction.
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    const base = statusFilter === "ALL" ? items : items.filter((p) => p.status === statusFilter);
    if (!sortBy) return base;
    const valOf = (po) => {
      switch (sortBy) {
        case "number": return po.number || "";
        case "supplier": return (po.supplier_snapshot?.name || "").toLowerCase();
        case "category": return (po.category || "").toLowerCase();
        case "date": return po.date || "";
        case "total_ttc": return po.total_ttc || 0;
        case "status": return po.status || "";
        default: return "";
      }
    };
    const sorted = [...base].sort((a, b) => {
      const va = valOf(a); const vb = valOf(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [items, statusFilter, sortBy, sortDir]);

  const openCreate = () => {
    setCreating({
      supplier_id: suppliers[0]?.supplier_id || "",
      date: new Date().toISOString().slice(0, 10),
      delivery_date: "",
      currency: "MAD",
      notes: "",
      lines: [blankLine()],
    });
    setScanResult(null);
    setScanInputKey((k) => k + 1);
  };

  // Iter-228 — OCR a supplier devis/facture to pre-fill the BC form.
  const runScanDocument = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/outgoing-pos/scan-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const d = r.data || {};
      setScanResult(d);
      // Pre-fill the form. If supplier matched, use that supplier_id;
      // otherwise mark `supplier_id` as the special "__scan_new__" marker
      // (the save() flow will POST /suppliers first when it sees this).
      const matchedSup = d.supplier_match?.matched ? d.supplier_match.supplier : null;
      const proposed = d.supplier_match?.proposed || {};
      const lines = (d.lines && d.lines.length > 0) ? d.lines : [blankLine()];
      setCreating((c) => ({
        ...(c || {}),
        supplier_id: matchedSup?.supplier_id || "__scan_new__",
        proposed_supplier: matchedSup ? null : proposed,
        scan_supplier_match: d.supplier_match,
        scan_document_meta: d.document,
        date: d.document?.date || c?.date || new Date().toISOString().slice(0, 10),
        delivery_date: d.document?.delivery_date || c?.delivery_date || "",
        currency: d.document?.currency || c?.currency || "MAD",
        notes: d.document?.number
          ? `Réf. ${d.document.type === "QUOTE" ? "devis" : "facture"} fournisseur : ${d.document.number}`
          : (c?.notes || ""),
        lines,
      }));
      if (d.ocr_error) {
        toast.warning("OCR partiel — vérifiez les champs extraits.");
      } else if (!matchedSup) {
        toast.success("Document scanné — fournisseur à créer (champs pré-remplis).");
      } else {
        toast.success(`Document scanné — fournisseur existant détecté : ${matchedSup.name}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du scan OCR");
    } finally {
      setScanning(false);
      setScanInputKey((k) => k + 1);
    }
  };

  const clearScan = () => {
    setScanResult(null);
    setScanInputKey((k) => k + 1);
    setCreating((c) => c ? ({
      ...c,
      supplier_id: suppliers[0]?.supplier_id || "",
      proposed_supplier: null,
      scan_supplier_match: null,
      scan_document_meta: null,
      lines: [blankLine()],
      notes: "",
    }) : c);
  };

  // Iter-206i — open the same dialog in EDIT mode (used for DRAFT or ISSUED).
  const openEdit = (po) => {
    setCreating({
      po_id: po.po_id,                 // marker → save will PATCH instead of POST
      number: po.number || null,        // null on DRAFT, set on ISSUED (display-only)
      status: po.status,
      supplier_id: po.supplier_id || suppliers[0]?.supplier_id || "",
      supplier_snapshot: po.supplier_snapshot || null,
      date: po.date || new Date().toISOString().slice(0, 10),
      delivery_date: po.delivery_date || "",
      currency: po.currency || "MAD",
      notes: po.notes || "",
      // Iter-206z — context for post-send edits.
      revision_count: po.revision_count || 0,
      revision_note: "",
      resent_count: po.resent_count || 0,
      lines: (po.lines || [blankLine()]).map((ln) => ({
        description: ln.description || "",
        category: ln.category || "Logiciel / SaaS",
        unit: ln.unit || "U",
        quantity: ln.quantity ?? 1,
        rate: ln.rate ?? "",
        vat_rate: ln.vat_rate ?? 0.2,
      })),
    });
  };

  const updateLine = (i, patch) => {
    setCreating((c) => ({ ...c, lines: c.lines.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln) }));
  };

  // Iter-206i — when user types the TTC unit price, derive PU HT (the
  // canonical stored amount) so the rest of the form stays consistent.
  const updateLineTtc = (i, ttcStr) => {
    setCreating((c) => ({
      ...c,
      lines: c.lines.map((ln, idx) => {
        if (idx !== i) return ln;
        const ttc = parseFloat(ttcStr);
        if (!Number.isFinite(ttc) || ttc <= 0) return { ...ln, rate: ttcStr === "" ? "" : ln.rate };
        const vat = parseFloat(ln.vat_rate) || 0;
        const ht = ttc / (1 + vat);
        return { ...ln, rate: Number(ht.toFixed(4)) };
      }),
    }));
  };

  const totals = useMemo(() => {
    const lines = creating?.lines || [];
    let ht = 0, vat = 0;
    for (const ln of lines) {
      const lh = (parseFloat(ln.quantity) || 0) * (parseFloat(ln.rate) || 0);
      ht += lh;
      vat += lh * (parseFloat(ln.vat_rate) || 0);
    }
    return { ht: ht.toFixed(2), vat: vat.toFixed(2), ttc: (ht + vat).toFixed(2) };
  }, [creating]);

  const save = async (issueAfter) => {
    if (!creating.supplier_id) return toast.error("Sélectionnez un fournisseur.");
    if (!creating.lines.length || creating.lines.some((ln) => !ln.description?.trim() || !ln.quantity)) {
      return toast.error("Chaque ligne doit avoir une description et une quantité.");
    }
    // Iter-228 — when supplier_id is the "__scan_new__" marker, first
    // create the supplier from the OCR-extracted fields the user may have
    // edited in the proposed-supplier card.
    let effectiveSupplierId = creating.supplier_id;
    if (effectiveSupplierId === "__scan_new__") {
      const prop = creating.proposed_supplier || {};
      const name = (prop.name || "").trim();
      if (!name) {
        return toast.error("Renseignez au minimum le nom du nouveau fournisseur.");
      }
      try {
        const payloadSup = {
          name,
          ice: prop.ice || null,
          address: prop.address || null,
          contact_email: prop.contact_email || null,
          contact_phone: prop.contact_phone || null,
          default_currency: prop.default_currency || creating.currency || "MAD",
        };
        const r = await api.post("/suppliers", payloadSup);
        effectiveSupplierId = r.data?.supplier_id;
        if (!effectiveSupplierId) throw new Error("supplier_id manquant");
        toast.success(`Fournisseur « ${name} » créé`);
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Échec de la création du fournisseur");
        return;
      }
    }
    const payload = {
      supplier_id: effectiveSupplierId,
      date: creating.date || null,
      delivery_date: creating.delivery_date || null,
      currency: creating.currency,
      notes: creating.notes || "",
      lines: creating.lines.map((ln) => ({
        description: ln.description,
        category: ln.category,
        unit: ln.unit || "U",
        quantity: parseFloat(ln.quantity) || 0,
        rate: parseFloat(ln.rate) || 0,
        vat_rate: parseFloat(ln.vat_rate) || 0,
      })),
    };
    try {
      let poId;
      // Iter-206z — detect whether the edit lands on a BC already sent. When
      // true, the payload carries `revision_note` so the backend appends a
      // `revisions[]` entry. The footer button label switches to
      // "Enregistrer et envoyer la nouvelle version".
      const isPostSendEdit = !!creating.po_id && (creating.status === "SENT" || creating.status === "RECEIVED");
      if (creating.po_id) {
        const patchPayload = { ...payload };
        // Iter-207k — include the (possibly empty) BC number when status is
        // DRAFT or ISSUED so the user can rename it. The backend rejects any
        // change once SENT or beyond.
        if (creating.status === "DRAFT" || creating.status === "ISSUED") {
          const trimmedNumber = (creating.number || "").trim();
          if (trimmedNumber) {
            patchPayload.number = trimmedNumber;
          } else if (creating.status === "ISSUED") {
            return toast.error("Le numéro ne peut pas être vide sur un BC déjà émis.");
          }
        }
        if (isPostSendEdit) {
          patchPayload.revision_note = (creating.revision_note || "").trim();
          if (!patchPayload.revision_note) {
            toast.error("Veuillez indiquer la raison de la modification (note de révision).");
            return;
          }
        }
        await api.patch(`/outgoing-pos/${creating.po_id}`, patchPayload);
        poId = creating.po_id;
        toast.success(isPostSendEdit
          ? `BC mis à jour (rév. ${(creating.revision_count || 0) + 1})`
          : "Bon de commande mis à jour"
        );
      } else {
        const r = await api.post("/outgoing-pos", payload);
        poId = r.data.po_id;
        toast.success("Bon de commande créé");
      }
      // Iter-206z — when issueAfter is requested for a post-send edit, it means
      // "save + resend the new version" (re-uses the existing /send endpoint
      // which now accepts SENT/RECEIVED for resends).
      if (issueAfter) {
        if (isPostSendEdit) {
          try {
            const presend = await api.get(`/outgoing-pos/${poId}/send/preview`);
            await api.post(`/outgoing-pos/${poId}/send`, {
              to_email: presend.data?.to_email || creating.supplier_snapshot?.contact_email || undefined,
              subject: presend.data?.subject || undefined,
              body: presend.data?.body_text || null,
            });
            toast.success("Nouvelle version envoyée au fournisseur");
          } catch (sendErr) {
            const sd = sendErr?.response?.data?.detail;
            if (sd && typeof sd === "object" && sd.code === "supplier_email_missing") {
              setMissingEmail({
                po: { ...creating, po_id: poId },
                supplier_id: sd.supplier_id,
                supplier_name: sd.supplier_name,
                email: "",
                save_on_supplier: true,
                submitting: false,
              });
              setCreating(null);
              load();
              return;
            }
            toast.error(typeof sd === "string" ? sd : "Échec de l'envoi de la nouvelle version");
          }
        } else if (!creating.po_id || creating.status === "DRAFT") {
          await api.post(`/outgoing-pos/${poId}/issue`);
          toast.success("BC émis");
        }
      }
      setCreating(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (Array.isArray(d) ? d.map(x => x.msg || x).join(", ") : "Échec"));
    }
  };

  const issue = async (po) => {
    if (!window.confirm(`Émettre le bon de commande ? Un numéro lui sera attribué. Il restera modifiable tant qu'il n'a pas été envoyé.`)) return;
    try {
      const r = await api.post(`/outgoing-pos/${po.po_id}/issue`);
      toast.success(`BC émis : ${r.data.number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  // Iter-206c — suppression dure d'un BC déjà annulé.
  const hardDelete = async (po) => {
    if (!window.confirm(`Supprimer définitivement le BC ${po.number || "(brouillon)"} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/outgoing-pos/${po.po_id}`);
      toast.success("BC supprimé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la suppression"); }
  };

  const cancel = async (po) => {
    // Iter-206b — un brouillon est supprimé définitivement (aucune trace),
    // les autres statuts sont marqués comme "Annulé" avec motif.
    if (po.status === "DRAFT") {
      if (!window.confirm("Supprimer définitivement ce brouillon ?")) return;
      try {
        await api.post(`/outgoing-pos/${po.po_id}/cancel?reason=draft+discard`);
        toast.success("Brouillon supprimé");
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
      return;
    }
    const reason = window.prompt("Motif d'annulation (facultatif) :", "") || "";
    if (!window.confirm(`Annuler définitivement ce BC ?`)) return;
    try {
      await api.post(`/outgoing-pos/${po.po_id}/cancel?reason=${encodeURIComponent(reason)}`);
      toast.success("BC annulé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const markReceived = async (po) => {
    if (!window.confirm("Confirmer la réception (AR fournisseur) ?")) return;
    try {
      await api.post(`/outgoing-pos/${po.po_id}/receive`);
      toast.success("BC marqué comme reçu");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const download = async (po) => {
    try {
      const r = await api.get(`/outgoing-pos/${po.po_id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po.number || "BC"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec du téléchargement"); }
  };

  const openSend = async (po) => {
    // Pre-fetch the rendered template so the dialog opens with subject + body
    // already populated from the CEO-configured template (iter-206o).
    try {
      const r = await api.get(`/outgoing-pos/${po.po_id}/send/preview`);
      const d = r.data || {};
      setSending({
        po,
        to: d.to_email || po.supplier_snapshot?.contact_email || "",
        subject: d.subject || `[BAIM] Bon de commande ${po.number || ""}`.trim(),
        body: d.body_text || "",
        ccEmails: d.cc_emails || [],
        fromAlias: d.from_alias || "",
      });
    } catch (e) {
      // Fallback to a minimal default if the preview endpoint fails (e.g.
      // permissions). The send endpoint will still apply the template.
      setSending({
        po,
        to: po.supplier_snapshot?.contact_email || "",
        subject: `[BAIM] Bon de commande ${po.number || ""}`.trim(),
        body: "",
      });
    }
  };

  const doSend = async () => {
    try {
      await api.post(`/outgoing-pos/${sending.po.po_id}/send`, {
        to_email: sending.to,
        subject: sending.subject,
        body: sending.body || null,
      });
      toast.success("BC envoyé par email");
      setSending(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Iter-206y — typed 400 when supplier has no email on file.
      if (d && typeof d === "object" && d.code === "supplier_email_missing") {
        setMissingEmail({
          po: sending.po,
          supplier_id: d.supplier_id,
          supplier_name: d.supplier_name,
          email: "",
          save_on_supplier: true,
          submitting: false,
        });
        return;
      }
      toast.error(typeof d === "string" ? d : "Échec de l'envoi");
    }
  };

  // Iter-206y — after user fills in the missing supplier email, call /send again.
  const submitMissingEmail = async () => {
    if (!missingEmail.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(missingEmail.email)) {
      toast.error("Email invalide");
      return;
    }
    setMissingEmail({ ...missingEmail, submitting: true });
    try {
      const r = await api.post(`/outgoing-pos/${missingEmail.po.po_id}/send`, {
        to_email: missingEmail.email,
        subject: sending?.subject || undefined,
        body: sending?.body || null,
        save_email_on_supplier: !!missingEmail.save_on_supplier,
      });
      const savedOnSupplier = r?.data?._supplier_email_saved === true;
      toast.success(savedOnSupplier
        ? `BC envoyé. Email "${missingEmail.email}" enregistré sur le fournisseur.`
        : "BC envoyé par email"
      );
      setMissingEmail(null);
      setSending(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de l'envoi");
      setMissingEmail({ ...missingEmail, submitting: false });
    }
  };

  // Iter-206y — manual "mark as sent" (WhatsApp / paper / external email).
  const submitMarkSent = async () => {
    setMarkingSent({ ...markingSent, submitting: true });
    try {
      await api.post(`/outgoing-pos/${markingSent.po.po_id}/mark-sent`, {
        channel: markingSent.channel || "manuel",
        recipient: markingSent.recipient || null,
        note: markingSent.note || null,
      });
      toast.success("BC marqué comme envoyé");
      setMarkingSent(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec du marquage");
      setMarkingSent({ ...markingSent, submitting: false });
    }
  };

  // Iter-206 — Receive supplier invoice flow (with OCR + PO number verification).
  // Iter-217 — Auto-compute the empty (3rd) amount field when the other two are set.
  // Rule: HT + VAT = TTC. We only fill the lone zero — never the field the user
  // just edited, so typing is not "fought" by the auto-fill.
  const updateInvoiceAmount = (field, raw) => {
    setReceivingInvoice((s) => {
      if (!s) return s;
      const form = { ...s.form, [field]: raw };
      const ht = parseFloat(form.amount_ht) || 0;
      const vat = parseFloat(form.amount_vat) || 0;
      const ttc = parseFloat(form.amount_ttc) || 0;
      const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
      if (field !== "amount_ttc" && ht > 0 && vat > 0 && ttc === 0) {
        form.amount_ttc = fmt(ht + vat);
      } else if (field !== "amount_vat" && ht > 0 && ttc > 0 && vat === 0 && ttc >= ht) {
        form.amount_vat = fmt(ttc - ht);
      } else if (field !== "amount_ht" && vat > 0 && ttc > 0 && ht === 0 && ttc >= vat) {
        form.amount_ht = fmt(ttc - vat);
      }
      return { ...s, form };
    });
  };

  const openReceiveInvoice = (po) => {
    setReceivingInvoice({
      po,
      step: "upload",     // upload → review → done
      file: null,
      uploading: false,
      draft: null,
      staged: null,
      verif: null,
      ocrError: null,
      form: {
        invoice_number: "",
        invoice_date: new Date().toISOString().slice(0, 10),
        amount_ht: 0,
        amount_vat: 0,
        amount_ttc: 0,
        currency: po.currency || "MAD",
        notes: "",
        force: false,
      },
    });
  };

  const runInvoiceOcr = async (file) => {
    if (!file || !receivingInvoice) return;
    setReceivingInvoice((s) => ({ ...s, uploading: true, file }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/outgoing-pos/${receivingInvoice.po.po_id}/invoice-ocr`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      const d = r.data?.draft || {};
      const v = r.data?.verification || {};
      setReceivingInvoice((s) => ({
        ...s,
        uploading: false,
        step: "review",
        draft: d,
        staged: r.data?.staged_file,
        verif: v,
        ocrError: r.data?.ocr_error || null,
        form: {
          ...s.form,
          invoice_number: d.supplier_invoice_number || s.form.invoice_number,
          invoice_date: d.invoice_date || s.form.invoice_date,
          amount_ht: Number(d.amount_ht ?? s.form.amount_ht) || 0,
          amount_vat: Number(d.amount_vat ?? s.form.amount_vat) || 0,
          amount_ttc: Number(d.amount_ttc ?? s.form.amount_ttc) || 0,
          currency: d.currency || s.form.currency,
          force: !v?.found,
        },
      }));
      if (r.data?.ocr_error) {
        toast.warning("OCR partiel — vérifiez les champs extraits.");
      } else if (!v?.found) {
        toast.warning(`Le numéro ${v?.po_number || "BC"} n'a pas été détecté dans la facture.`);
      } else {
        toast.success("Facture scannée avec succès");
      }
    } catch (e) {
      setReceivingInvoice((s) => ({ ...s, uploading: false }));
      toast.error(e?.response?.data?.detail || "Échec de l'OCR");
    }
  };

  const submitReceiveInvoice = async () => {
    const s = receivingInvoice;
    if (!s?.staged?.staged_file_id) return toast.error("Aucun fichier scanné.");
    if (!s.form.invoice_number?.trim()) return toast.error("N° de facture requis.");
    if (!s.form.invoice_date) return toast.error("Date de facture requise.");
    if (!(parseFloat(s.form.amount_ttc) > 0)) return toast.error("Montant TTC > 0 requis.");
    try {
      await api.post(`/outgoing-pos/${s.po.po_id}/receive-invoice`, {
        staged_file_id: s.staged.staged_file_id,
        invoice_number: s.form.invoice_number.trim(),
        invoice_date: s.form.invoice_date,
        amount_ht: parseFloat(s.form.amount_ht) || 0,
        amount_vat: parseFloat(s.form.amount_vat) || 0,
        amount_ttc: parseFloat(s.form.amount_ttc) || 0,
        currency: (s.form.currency || "MAD").toUpperCase(),
        notes: s.form.notes || null,
        force: !!s.form.force,
      });
      toast.success("Facture enregistrée — BC passé en « Facture reçue »");
      setReceivingInvoice(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    }
  };

  const downloadInvoice = async (po) => {
    try {
      const r = await api.get(`/outgoing-pos/${po.po_id}/invoice-file`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = po.received_invoice?.file_original_name || `facture_${po.number || po.po_id}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du téléchargement de la facture");
    }
  };

  // Iter-206 — Mark paid flow.
  const openMarkPaid = (po) => {
    setPaying({
      po,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: "Virement",
      payment_reference: "",
      notes: "",
    });
  };

  const submitMarkPaid = async () => {
    if (!paying?.payment_date) return toast.error("Date de paiement requise.");
    try {
      const r = await api.post(`/outgoing-pos/${paying.po.po_id}/mark-paid`, {
        payment_date: paying.payment_date,
        payment_method: paying.payment_method || null,
        payment_reference: paying.payment_reference || null,
        notes: paying.notes || null,
      });
      const fallback = r.data?.cabinet_line?.booking_period_fallback;
      toast.success(
        fallback
          ? "BC payé — dépense imputée sur le mois courant (note d'origine verrouillée)"
          : "BC payé — dépense ajoutée aux dépenses du cabinet",
      );
      setPaying(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du marquage payé");
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px] space-y-6" data-testid="outgoing-pos-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Bons de commande sortants</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Émettez et envoyez vos bons de commande à vos fournisseurs.
            Pensez à <Link to="/suppliers" className="underline">gérer votre carnet de fournisseurs</Link> au préalable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-40">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="outgoing-pos-status-filter" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tous statuts</SelectItem>
                {(meta.statuses || []).map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={openCreate} disabled={suppliers.length === 0} data-testid="outgoing-po-new-btn">
            <Plus size={14} className="mr-1.5" /> Nouveau BC
          </Button>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-sm text-zinc-500 text-center py-16">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 text-center py-16 border border-dashed border-zinc-200 rounded-lg">
          {items.length === 0 ? "Aucun bon de commande. Créez-en un pour commencer." : "Aucun BC pour ce filtre."}
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              <tr>
                <th className="text-left px-4 py-2 cursor-pointer hover:text-brand-navy select-none" onClick={() => toggleSort("number")} data-testid="sort-number">
                  <span className="inline-flex items-center gap-1">N° {sortBy === "number" ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-30" />}</span>
                </th>
                <th className="text-left px-4 py-2 cursor-pointer hover:text-brand-navy select-none" onClick={() => toggleSort("supplier")} data-testid="sort-supplier">
                  <span className="inline-flex items-center gap-1">Fournisseur {sortBy === "supplier" ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-30" />}</span>
                </th>
                <th className="text-left px-4 py-2 cursor-pointer hover:text-brand-navy select-none" onClick={() => toggleSort("category")} data-testid="sort-category">
                  <span className="inline-flex items-center gap-1">Catégorie {sortBy === "category" ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-30" />}</span>
                </th>
                <th className="text-left px-4 py-2 cursor-pointer hover:text-brand-navy select-none" onClick={() => toggleSort("date")} data-testid="sort-date">
                  <span className="inline-flex items-center gap-1">Date {sortBy === "date" ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-30" />}</span>
                </th>
                <th className="text-right px-4 py-2 cursor-pointer hover:text-brand-navy select-none" onClick={() => toggleSort("total_ttc")} data-testid="sort-total">
                  <span className="inline-flex items-center gap-1 justify-end w-full">Total TTC {sortBy === "total_ttc" ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-30" />}</span>
                </th>
                <th className="text-left px-4 py-2 cursor-pointer hover:text-brand-navy select-none" onClick={() => toggleSort("status")} data-testid="sort-status">
                  <span className="inline-flex items-center gap-1">Statut {sortBy === "status" ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-30" />}</span>
                </th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((p) => {
                const sm = STATUS_META[p.status] || {};
                // Iter-206c — extract distinct categories from the PO lines.
                const cats = Array.from(new Set((p.lines || []).map((l) => l.category).filter(Boolean)));
                const catLabel = cats.length === 0 ? "—" : cats.length === 1 ? cats[0] : `${cats[0]} +${cats.length - 1}`;
                return (
                  <tr key={p.po_id} className="hover:bg-zinc-50/50" data-testid={`outgoing-po-row-${p.po_id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{p.number || <em className="text-zinc-400">brouillon</em>}</td>
                    <td className="px-4 py-3 font-medium">{p.supplier_snapshot?.name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600" title={cats.join(", ")} data-testid={`outgoing-po-category-${p.po_id}`}>{catLabel}</td>
                    <td className="px-4 py-3 text-xs">{p.date || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{Number(p.total_ttc || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} {p.currency}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${sm.color}`}>{sm.label || p.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => download(p)} title="Télécharger le PDF" data-testid={`outgoing-po-download-${p.po_id}`}>
                        <Download size={12} />
                      </Button>
                      {/* Iter-206y — édition possible tant que la facture n'est pas reçue. */}
                      {(p.status === "DRAFT" || p.status === "ISSUED" || p.status === "SENT" || p.status === "RECEIVED") && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)} title="Modifier" data-testid={`outgoing-po-edit-${p.po_id}`}>
                          <Pencil size={12} />
                        </Button>
                      )}
                      {p.status === "DRAFT" && (
                        <Button variant="ghost" size="sm" onClick={() => issue(p)} title="Émettre" data-testid={`outgoing-po-issue-${p.po_id}`}>
                          <FileText size={12} />
                        </Button>
                      )}
                      {(p.status === "DRAFT" || p.status === "ISSUED") && (
                        <Button variant="ghost" size="sm" onClick={() => openSend(p)} title="Envoyer par email" data-testid={`outgoing-po-send-${p.po_id}`}>
                          <Send size={12} />
                        </Button>
                      )}
                      {/* Iter-206y — marquer comme envoyé manuellement (hors plateforme). */}
                      {(p.status === "DRAFT" || p.status === "ISSUED") && (
                        <Button variant="ghost" size="sm" onClick={() => setMarkingSent({ po: p, channel: "manuel", recipient: "", note: "" })} title="Marquer comme envoyé manuellement (WhatsApp / Papier / Autre)" data-testid={`outgoing-po-mark-sent-${p.po_id}`}>
                          <Check size={12} />
                        </Button>
                      )}
                      {p.status === "SENT" && (
                        <Button variant="ghost" size="sm" onClick={() => markReceived(p)} title="Marquer reçu (AR)" data-testid={`outgoing-po-receive-${p.po_id}`}>
                          <MailOpen size={12} />
                        </Button>
                      )}
                      {(p.status === "SENT" || p.status === "RECEIVED") && (
                        <Button variant="ghost" size="sm" onClick={() => openReceiveInvoice(p)} title="Recevoir facture fournisseur" data-testid={`outgoing-po-receive-invoice-${p.po_id}`}>
                          <ScanLine size={12} />
                        </Button>
                      )}
                      {(p.status === "INVOICE_RECEIVED" || p.status === "PAID") && (
                        <Button variant="ghost" size="sm" onClick={() => downloadInvoice(p)} title="Télécharger la facture" data-testid={`outgoing-po-invoice-file-${p.po_id}`}>
                          <FileCheck size={12} />
                        </Button>
                      )}
                      {p.status === "INVOICE_RECEIVED" && (
                        <Button variant="ghost" size="sm" onClick={() => openReceiveInvoice(p)} title="Remplacer la facture fournisseur" data-testid={`outgoing-po-replace-invoice-${p.po_id}`}>
                          <RotateCcw size={12} />
                        </Button>
                      )}
                      {p.status === "INVOICE_RECEIVED" && (
                        <Button variant="ghost" size="sm" onClick={() => openMarkPaid(p)} title="Marquer la facture comme payée" data-testid={`outgoing-po-mark-paid-${p.po_id}`}>
                          <Coins size={12} />
                        </Button>
                      )}
                      {p.status !== "CANCELLED" && p.status !== "PAID" && (
                        <Button variant="ghost" size="sm" onClick={() => cancel(p)} className="text-rose-600 hover:text-rose-700" title={p.status === "DRAFT" ? "Supprimer le brouillon" : "Annuler"} data-testid={`outgoing-po-cancel-${p.po_id}`}>
                          {p.status === "DRAFT" ? <Trash2 size={12} /> : <Ban size={12} />}
                        </Button>
                      )}
                      {p.status === "CANCELLED" && (
                        <Button variant="ghost" size="sm" onClick={() => hardDelete(p)} className="text-rose-600 hover:text-rose-700" title="Supprimer définitivement" data-testid={`outgoing-po-hard-delete-${p.po_id}`}>
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={!!creating} onOpenChange={(o) => { if (!o) { setCreating(null); setScanResult(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="outgoing-po-create-dialog">
          <DialogHeader>
            <DialogTitle>
              {creating?.po_id
                ? `Modifier le bon de commande${creating.number ? ` ${creating.number}` : ""}`
                : "Nouveau bon de commande"}
            </DialogTitle>
          </DialogHeader>
          {creating && (
            <div className="space-y-4">
              {/* Iter-207k+n — manual BC number override + real-time uniqueness check.
                  Visible only while not-yet-sent (DRAFT / ISSUED). Hidden once SENT
                  or beyond. The check debounces 400ms after each keystroke. */}
              {creating.po_id && (creating.status === "DRAFT" || creating.status === "ISSUED") && (
                <NumberFieldWithCheck
                  poId={creating.po_id}
                  value={creating.number || ""}
                  status={creating.status}
                  onChange={(v) => setCreating({ ...creating, number: v })}
                />
              )}

              {/* Iter-228 — Scan a supplier devis/facture to pre-fill this form (new BC only). */}
              {!creating.po_id && (
                <div className="border border-dashed border-zinc-300 rounded-lg p-4 bg-gradient-to-br from-zinc-50 to-white" data-testid="po-scan-block">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <ScanLine size={18} className="text-brand-navy" />
                      <div>
                        <div className="text-sm font-semibold">Scanner un devis / une facture</div>
                        <div className="text-[11px] text-zinc-500 leading-relaxed">
                          OCR Gemini · pré-remplit le fournisseur, les lignes et les montants. Le fournisseur est créé automatiquement s&apos;il n&apos;existe pas.
                        </div>
                      </div>
                    </div>
                    {scanResult && (
                      <Button variant="ghost" size="sm" onClick={clearScan} title="Effacer le scan" data-testid="po-scan-clear-btn">
                        <X size={14} className="mr-1" /> Effacer
                      </Button>
                    )}
                  </div>
                  {!scanResult && (
                    <Input
                      key={`scan-input-${scanInputKey}`}
                      type="file"
                      accept="image/*,application/pdf,.heic,.heif"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) runScanDocument(f);
                      }}
                      disabled={scanning}
                      data-testid="po-scan-file-input"
                    />
                  )}
                  {scanning && (
                    <div className="text-xs text-zinc-600 mt-2 flex items-center gap-2">
                      <Loader2 size={12} className="animate-spin" /> Analyse OCR en cours…
                    </div>
                  )}
                  {scanResult && (
                    <div className="text-[11px] text-zinc-700 mt-1 space-y-1">
                      <div className="font-mono">
                        Document : <strong>{scanResult.document?.type === "QUOTE" ? "Devis" : "Facture"}</strong>
                        {scanResult.document?.number && <> · N° <strong>{scanResult.document.number}</strong></>}
                        {scanResult.document?.date && <> · Date {scanResult.document.date}</>}
                      </div>
                      {scanResult.ocr_error ? (
                        <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                          OCR partiel : {scanResult.ocr_error}. Vérifiez les champs ci-dessous.
                        </div>
                      ) : scanResult.supplier_match?.matched ? (
                        <div className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center gap-1" data-testid="po-scan-supplier-matched">
                          <CheckCircle2 size={11} /> Fournisseur reconnu : <strong>{scanResult.supplier_match.supplier?.name}</strong>
                          {scanResult.supplier_match.match_type === "ice" && <span className="text-[10px] text-emerald-600">(via ICE)</span>}
                          {scanResult.supplier_match.match_type === "name" && <span className="text-[10px] text-emerald-600">(via nom)</span>}
                        </div>
                      ) : (
                        <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1" data-testid="po-scan-supplier-new">
                          <AlertTriangle size={11} /> Fournisseur inconnu : sera créé à l&apos;enregistrement du BC sous le nom <strong>« {creating?.proposed_supplier?.name || "—"} »</strong>.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Iter-228 — when a NEW supplier is being created from the scan, replace the
                  regular supplier-select with an inline editable card. The user can review
                  and tweak the OCR-extracted fields (name, ICE, address, email, phone) before
                  the form save() calls POST /api/suppliers automatically. */}
              {creating.supplier_id === "__scan_new__" ? (
                <div className="border border-amber-300 bg-amber-50/60 rounded-lg p-3 space-y-2" data-testid="po-new-supplier-card">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
                      <Plus size={12} /> Nouveau fournisseur (créé automatiquement)
                    </div>
                    <button
                      type="button"
                      className="text-[11px] text-zinc-600 hover:text-brand-navy underline"
                      onClick={() => setCreating((c) => ({ ...c, supplier_id: suppliers[0]?.supplier_id || "", proposed_supplier: null }))}
                      data-testid="po-new-supplier-pick-existing-btn"
                    >
                      Choisir un fournisseur existant
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px]">Raison sociale <span className="text-red-600">*</span></Label>
                      <Input
                        value={creating.proposed_supplier?.name || ""}
                        onChange={(e) => setCreating({ ...creating, proposed_supplier: { ...(creating.proposed_supplier || {}), name: e.target.value } })}
                        data-testid="po-new-supplier-name"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px]">ICE</Label>
                      <Input
                        value={creating.proposed_supplier?.ice || ""}
                        onChange={(e) => setCreating({ ...creating, proposed_supplier: { ...(creating.proposed_supplier || {}), ice: e.target.value } })}
                        data-testid="po-new-supplier-ice"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px]">Adresse</Label>
                    <Input
                      value={creating.proposed_supplier?.address || ""}
                      onChange={(e) => setCreating({ ...creating, proposed_supplier: { ...(creating.proposed_supplier || {}), address: e.target.value } })}
                      data-testid="po-new-supplier-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px]">Email contact</Label>
                      <Input
                        value={creating.proposed_supplier?.contact_email || ""}
                        onChange={(e) => setCreating({ ...creating, proposed_supplier: { ...(creating.proposed_supplier || {}), contact_email: e.target.value } })}
                        data-testid="po-new-supplier-email"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px]">Téléphone</Label>
                      <Input
                        value={creating.proposed_supplier?.contact_phone || ""}
                        onChange={(e) => setCreating({ ...creating, proposed_supplier: { ...(creating.proposed_supplier || {}), contact_phone: e.target.value } })}
                        data-testid="po-new-supplier-phone"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div /> {/* spacer to keep currency on the right like the regular layout */}
                    <div><Label>Devise</Label>
                      <Select value={creating.currency} onValueChange={(v) => setCreating({ ...creating, currency: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(meta.currencies || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fournisseur <span className="text-red-600">*</span></Label>
                  <Select value={creating.supplier_id} onValueChange={(v) => setCreating({ ...creating, supplier_id: v })}>
                    <SelectTrigger data-testid="po-form-supplier"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Devise</Label>
                  <Select value={creating.currency} onValueChange={(v) => setCreating({ ...creating, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(meta.currencies || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label>
                  <Input type="date" value={creating.date} onChange={(e) => setCreating({ ...creating, date: e.target.value })} />
                </div>
                <div><Label>Livraison prévue</Label>
                  <Input type="date" value={creating.delivery_date} onChange={(e) => setCreating({ ...creating, delivery_date: e.target.value })} />
                </div>
              </div>

              {/* Lines */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Lignes du BC</Label>
                  <Button size="sm" variant="outline" onClick={() => setCreating({ ...creating, lines: [...creating.lines, blankLine()] })} data-testid="po-form-add-line">
                    <Plus size={12} className="mr-1" /> Ajouter une ligne
                  </Button>
                </div>
                <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100">
                  {creating.lines.map((ln, i) => (
                    <div key={i} className="p-3 space-y-2 md:space-y-0 md:grid md:grid-cols-12 md:gap-2 md:items-end" data-testid={`po-form-line-${i}`}>
                      <div className="md:col-span-4">
                        <Label className="text-[10px]">Désignation</Label>
                        <Input value={ln.description} onChange={(e) => updateLine(i, { description: e.target.value })} />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-[10px]">Catégorie</Label>
                        <Select value={ln.category} onValueChange={(v) => updateLine(i, { category: v })}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(meta.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-4 gap-2 md:contents">
                        <div className="md:col-span-1">
                          <Label className="text-[10px]">Unité</Label>
                          <Input value={ln.unit} onChange={(e) => updateLine(i, { unit: e.target.value })} />
                        </div>
                        <div className="md:col-span-1">
                          <Label className="text-[10px]">Qté</Label>
                          <Input type="number" step="0.5" value={ln.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-[10px]">PU HT</Label>
                          <Input type="number" step="0.01" value={ln.rate} onChange={(e) => updateLine(i, { rate: e.target.value })} onFocus={(e) => e.target.select()} placeholder="0.00" data-testid={`po-form-line-${i}-rate`} />
                        </div>
                        <div className="md:col-span-1">
                          <Label className="text-[10px]">TVA</Label>
                          <Select value={String(ln.vat_rate)} onValueChange={(v) => updateLine(i, { vat_rate: parseFloat(v) })}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0%</SelectItem>
                              <SelectItem value="0.07">7%</SelectItem>
                              <SelectItem value="0.1">10%</SelectItem>
                              <SelectItem value="0.2">20%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {/* Iter-206i — PU TTC editable. Typing here back-fills PU HT. */}
                      <div className="md:col-span-12 grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] flex items-center gap-1">
                            PU TTC <span className="text-zinc-400 italic">(calculé / saisissable)</span>
                          </Label>
                          <Input
                            type="number" step="0.01"
                            value={(() => {
                              const r = parseFloat(ln.rate);
                              const v = parseFloat(ln.vat_rate) || 0;
                              if (!Number.isFinite(r)) return "";
                              return Number((r * (1 + v)).toFixed(2));
                            })()}
                            onChange={(e) => updateLineTtc(i, e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="0.00"
                            data-testid={`po-form-line-${i}-ttc`}
                          />
                        </div>
                        <div className="text-right text-[11px] text-zinc-500 self-end font-mono pb-2">
                          {(() => {
                            const q = parseFloat(ln.quantity) || 0;
                            const r = parseFloat(ln.rate) || 0;
                            const v = parseFloat(ln.vat_rate) || 0;
                            const lineTtc = q * r * (1 + v);
                            return `Total ligne TTC : ${lineTtc.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${creating.currency}`;
                          })()}
                        </div>
                      </div>
                      <div className="flex justify-end md:col-span-1 md:text-right">
                        <Button variant="ghost" size="sm" onClick={() => setCreating({ ...creating, lines: creating.lines.filter((_, idx) => idx !== i) })} className="text-rose-600">
                          <Trash2 size={14} className="mr-1 md:mr-0" />
                          <span className="md:hidden text-xs">Supprimer cette ligne</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-right text-sm space-y-0.5 font-mono">
                <div>Total HT : <strong>{totals.ht} {creating.currency}</strong></div>
                <div>TVA : <strong>{totals.vat} {creating.currency}</strong></div>
                <div className="text-base">Total TTC : <strong>{totals.ttc} {creating.currency}</strong></div>
              </div>

              <div><Label>Notes (facultatif)</Label>
                <Textarea rows={2} value={creating.notes} onChange={(e) => setCreating({ ...creating, notes: e.target.value })} />
              </div>

              {/* Iter-206z — Revision note, required when editing a sent BC. */}
              {creating?.po_id && (creating.status === "SENT" || creating.status === "RECEIVED") && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2" data-testid="revision-note-block">
                  <div className="flex items-center gap-2 text-xs font-semibold text-amber-900">
                    <AlertTriangle size={14} />
                    Modification d'un BC déjà envoyé · Révision {(creating.revision_count || 0) + 1}
                  </div>
                  <p className="text-[11px] text-amber-800 leading-relaxed">
                    Ce BC a déjà été envoyé au fournisseur. Décrivez la raison de la modification — elle sera persistée dans l'historique des révisions.
                  </p>
                  <Textarea
                    rows={2}
                    data-testid="revision-note-input"
                    value={creating.revision_note || ""}
                    onChange={(e) => setCreating({ ...creating, revision_note: e.target.value })}
                    placeholder="Ex. Correction du prix unitaire après accord fournisseur"
                    className="bg-white"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>Annuler</Button>
            {(() => {
              const isPostSend = creating?.po_id && (creating.status === "SENT" || creating.status === "RECEIVED");
              if (isPostSend) {
                // Iter-206z — 2 buttons for post-send edit:
                //   1) "Enregistrer uniquement" (no resend)
                //   2) "Enregistrer et envoyer la nouvelle version" (resend)
                return (
                  <>
                    <Button variant="outline" onClick={() => save(false)} data-testid="po-form-save-only">
                      Enregistrer uniquement
                    </Button>
                    <Button onClick={() => save(true)} data-testid="po-form-save-and-resend">
                      <Send size={12} className="mr-1.5" /> Enregistrer et envoyer la nouvelle version
                    </Button>
                  </>
                );
              }
              return (
                <>
                  <Button variant="outline" onClick={() => save(false)} data-testid="po-form-save-draft">
                    {creating?.po_id ? "Enregistrer les modifications" : "Enregistrer brouillon"}
                  </Button>
                  {(!creating?.po_id || creating?.status === "DRAFT") && (
                    <Button onClick={() => save(true)} data-testid="po-form-save-issue">
                      {creating?.po_id ? "Enregistrer et émettre" : "Émettre maintenant"}
                    </Button>
                  )}
                </>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={!!sending} onOpenChange={(o) => { if (!o) setSending(null); }}>
        <DialogContent data-testid="outgoing-po-send-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer le BC par email</DialogTitle>
          </DialogHeader>
          {sending && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500">
                BC <strong>{sending.po.number || "(non émis)"}</strong> vers <strong>{sending.po.supplier_snapshot?.name}</strong>
              </div>
              {sending.fromAlias && (
                <div className="text-[11px] text-zinc-500 font-mono">
                  From: <span className="text-zinc-700">{sending.fromAlias}</span>
                  {sending.ccEmails && sending.ccEmails.length > 0 && (
                    <> · Cc: <span className="text-zinc-700">{sending.ccEmails.join(", ")}</span></>
                  )}
                </div>
              )}
              <div><Label>Destinataire</Label>
                <Input value={sending.to} onChange={(e) => setSending({ ...sending, to: e.target.value })} data-testid="send-to-input" />
              </div>
              <div><Label>Sujet</Label>
                <Input value={sending.subject} onChange={(e) => setSending({ ...sending, subject: e.target.value })} data-testid="send-subject-input" />
              </div>
              <div><Label>Message</Label>
                <Textarea rows={10} value={sending.body} onChange={(e) => setSending({ ...sending, body: e.target.value })} className="font-mono text-[11px] leading-relaxed" data-testid="send-body-input" />
                <div className="text-[10px] text-zinc-500 mt-1 italic">
                  Template configurable dans <strong>Configuration ▸ Templates ▸ Outgoing PO email template</strong> (CEO).
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSending(null)}>Annuler</Button>
            <Button onClick={doSend} data-testid="send-confirm-btn">
              <Send size={12} className="mr-1.5" /> Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-206 — Receive invoice dialog (OCR + verification) */}
      <Dialog open={!!receivingInvoice} onOpenChange={(o) => { if (!o) setReceivingInvoice(null); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="outgoing-po-receive-invoice-dialog">
          <DialogHeader>
            <DialogTitle>
              {receivingInvoice?.po?.status === "INVOICE_RECEIVED" ? "Remplacer la facture" : "Réception de facture"} — BC {receivingInvoice?.po?.number || ""}
            </DialogTitle>
          </DialogHeader>
          {receivingInvoice?.po?.status === "INVOICE_RECEIVED" && receivingInvoice?.po?.received_invoice && (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2.5 text-xs text-amber-900" data-testid="replace-invoice-warning">
              <strong>Remplacement</strong> · La facture actuelle <strong>{receivingInvoice.po.received_invoice.invoice_number}</strong> ({receivingInvoice.po.received_invoice.amount_ttc} {receivingInvoice.po.received_invoice.currency}) sera écrasée et l&apos;ancien fichier supprimé. L&apos;audit conserve la trace de l&apos;ancien numéro.
            </div>
          )}
          {receivingInvoice && receivingInvoice.step === "upload" && (
            <div className="space-y-4">
              <div className="text-xs text-zinc-500">
                Téléversez la facture du fournisseur (PDF, JPG, PNG, HEIC — max 10 MB).
                Le contenu sera analysé par OCR pour pré-remplir les champs et vérifier que le numéro de BC <strong>{receivingInvoice.po.number}</strong> apparaît bien sur le document.
              </div>
              <Input
                type="file"
                accept="image/*,application/pdf,.heic,.heif"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) runInvoiceOcr(f);
                }}
                disabled={receivingInvoice.uploading}
                data-testid="receive-invoice-file-input"
              />
              {receivingInvoice.uploading && (
                <div className="text-xs text-zinc-500 flex items-center gap-2">
                  <span className="animate-pulse">●</span> Analyse OCR en cours…
                </div>
              )}
            </div>
          )}
          {receivingInvoice && receivingInvoice.step === "review" && (
            <div className="space-y-3">
              {/* Verification banner */}
              {receivingInvoice.verif?.found ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-start gap-2" data-testid="invoice-verif-ok">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                  <div>N° BC <strong>{receivingInvoice.verif.po_number}</strong> détecté sur la facture.</div>
                </div>
              ) : (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="invoice-verif-missing">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <div>
                    Le numéro de BC <strong>{receivingInvoice.verif?.po_number}</strong> n'a pas été détecté dans le document.
                    Vérifiez qu'il s'agit bien de la bonne facture. Cochez « Forcer l'enregistrement » pour continuer.
                  </div>
                </div>
              )}
              {receivingInvoice.ocrError && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                  OCR partiel : {receivingInvoice.ocrError}. Remplissez les champs manuellement.
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>N° de facture fournisseur <span className="text-red-600">*</span></Label>
                  <Input
                    value={receivingInvoice.form.invoice_number}
                    onChange={(e) => setReceivingInvoice({ ...receivingInvoice, form: { ...receivingInvoice.form, invoice_number: e.target.value } })}
                    data-testid="invoice-number-input"
                  />
                </div>
                <div>
                  <Label>Date de facture <span className="text-red-600">*</span></Label>
                  <Input
                    type="date"
                    value={receivingInvoice.form.invoice_date}
                    onChange={(e) => setReceivingInvoice({ ...receivingInvoice, form: { ...receivingInvoice.form, invoice_date: e.target.value } })}
                    data-testid="invoice-date-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Montant HT</Label>
                  <Input type="number" step="0.01" value={receivingInvoice.form.amount_ht}
                    onChange={(e) => updateInvoiceAmount("amount_ht", e.target.value)} />
                </div>
                <div>
                  <Label>Montant TVA</Label>
                  <Input type="number" step="0.01" value={receivingInvoice.form.amount_vat}
                    onChange={(e) => updateInvoiceAmount("amount_vat", e.target.value)} />
                </div>
                <div>
                  <Label>Montant TTC <span className="text-red-600">*</span></Label>
                  <Input type="number" step="0.01" value={receivingInvoice.form.amount_ttc}
                    onChange={(e) => updateInvoiceAmount("amount_ttc", e.target.value)}
                    data-testid="invoice-amount-ttc-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Devise</Label>
                  <Select value={receivingInvoice.form.currency} onValueChange={(v) => setReceivingInvoice({ ...receivingInvoice, form: { ...receivingInvoice.form, currency: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(meta.currencies || ["MAD", "EUR", "USD"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notes (facultatif)</Label>
                <Textarea rows={2} value={receivingInvoice.form.notes}
                  onChange={(e) => setReceivingInvoice({ ...receivingInvoice, form: { ...receivingInvoice.form, notes: e.target.value } })} />
              </div>

              {!receivingInvoice.verif?.found && (
                <label className="flex items-center gap-2 text-xs text-zinc-700 select-none">
                  <input
                    type="checkbox"
                    checked={!!receivingInvoice.form.force}
                    onChange={(e) => setReceivingInvoice({ ...receivingInvoice, form: { ...receivingInvoice.form, force: e.target.checked } })}
                    data-testid="invoice-force-checkbox"
                  />
                  Forcer l'enregistrement malgré l'absence du N° BC sur la facture
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceivingInvoice(null)}>Annuler</Button>
            {receivingInvoice?.step === "review" && (
              <Button onClick={submitReceiveInvoice} data-testid="receive-invoice-confirm-btn">
                <FileCheck size={12} className="mr-1.5" /> Enregistrer la facture
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-206 — Mark paid dialog */}
      <Dialog open={!!paying} onOpenChange={(o) => { if (!o) setPaying(null); }}>
        <DialogContent data-testid="outgoing-po-mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Marquer la facture comme payée</DialogTitle>
          </DialogHeader>
          {paying && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500">
                BC <strong>{paying.po.number}</strong> — facture <strong>{paying.po.received_invoice?.invoice_number}</strong>
                {" "}/ {Number(paying.po.received_invoice?.amount_ttc || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })}{" "}
                {paying.po.received_invoice?.currency || paying.po.currency}.
                <br />
                Une ligne sera automatiquement créée dans les <strong>dépenses du cabinet</strong>.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date de paiement <span className="text-red-600">*</span></Label>
                  <Input
                    type="date"
                    value={paying.payment_date}
                    onChange={(e) => setPaying({ ...paying, payment_date: e.target.value })}
                    data-testid="mark-paid-date-input"
                  />
                </div>
                <div>
                  <Label>Méthode de paiement</Label>
                  <Select value={paying.payment_method} onValueChange={(v) => setPaying({ ...paying, payment_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Virement">Virement</SelectItem>
                      <SelectItem value="Chèque">Chèque</SelectItem>
                      <SelectItem value="Carte">Carte bancaire</SelectItem>
                      <SelectItem value="Cash">Espèces</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Référence (n° virement, chèque, …)</Label>
                <Input value={paying.payment_reference}
                  onChange={(e) => setPaying({ ...paying, payment_reference: e.target.value })} />
              </div>
              <div>
                <Label>Notes (facultatif)</Label>
                <Textarea rows={2} value={paying.notes}
                  onChange={(e) => setPaying({ ...paying, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaying(null)}>Annuler</Button>
            <Button onClick={submitMarkPaid} data-testid="mark-paid-confirm-btn">
              <Coins size={12} className="mr-1.5" /> Marquer payée
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-206y — Mark as sent manually */}
      <Dialog open={!!markingSent} onOpenChange={(o) => { if (!o) setMarkingSent(null); }}>
        <DialogContent className="max-w-md" data-testid="mark-sent-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check size={18} /> Marquer comme envoyé manuellement
            </DialogTitle>
          </DialogHeader>
          {markingSent && (
            <div className="space-y-3 text-sm">
              <div className="bg-zinc-50 border border-zinc-200 rounded p-3 text-xs text-zinc-700 leading-relaxed">
                Utilisez ceci si vous avez envoyé le BC <span className="font-mono">{markingSent.po.number || markingSent.po.po_id}</span> à votre fournisseur en dehors de la plateforme (WhatsApp, courrier papier, votre propre client email, etc.). Le statut passera à <span className="font-semibold">SENT</span> sans envoi Gmail.
              </div>
              <div>
                <Label>Canal d'envoi</Label>
                <Select
                  value={markingSent.channel}
                  onValueChange={(v) => setMarkingSent({ ...markingSent, channel: v })}
                >
                  <SelectTrigger data-testid="mark-sent-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email_externe">Email (client externe)</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="papier">Courrier papier</SelectItem>
                    <SelectItem value="manuel">Autre / Manuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destinataire <span className="text-zinc-400 text-xs">(facultatif)</span></Label>
                <Input
                  data-testid="mark-sent-recipient"
                  value={markingSent.recipient}
                  onChange={(e) => setMarkingSent({ ...markingSent, recipient: e.target.value })}
                  placeholder="ex. email, n° de tél, adresse"
                />
              </div>
              <div>
                <Label>Note <span className="text-zinc-400 text-xs">(facultatif)</span></Label>
                <Textarea
                  rows={2}
                  data-testid="mark-sent-note"
                  value={markingSent.note}
                  onChange={(e) => setMarkingSent({ ...markingSent, note: e.target.value })}
                  placeholder="Ex. envoyé via WhatsApp à 14h30"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkingSent(null)} disabled={markingSent?.submitting}>Annuler</Button>
            <Button onClick={submitMarkSent} disabled={markingSent?.submitting} data-testid="mark-sent-confirm-btn">
              <Check size={12} className="mr-1.5" /> {markingSent?.submitting ? "..." : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-206y — Supplier email missing modal */}
      <Dialog open={!!missingEmail} onOpenChange={(o) => { if (!o) setMissingEmail(null); }}>
        <DialogContent className="max-w-md" data-testid="missing-supplier-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} /> Email du fournisseur manquant
            </DialogTitle>
          </DialogHeader>
          {missingEmail && (
            <div className="space-y-3 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 leading-relaxed">
                Le fournisseur <span className="font-semibold">{missingEmail.supplier_name}</span> n'a pas d'adresse email enregistrée. Saisissez-la pour envoyer ce BC.
              </div>
              <div>
                <Label>Adresse email du fournisseur</Label>
                <Input
                  type="email"
                  data-testid="missing-email-input"
                  value={missingEmail.email}
                  onChange={(e) => setMissingEmail({ ...missingEmail, email: e.target.value })}
                  placeholder="contact@fournisseur.com"
                  autoFocus
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-xs text-zinc-700">
                <input
                  type="checkbox"
                  data-testid="missing-email-save-checkbox"
                  checked={missingEmail.save_on_supplier}
                  onChange={(e) => setMissingEmail({ ...missingEmail, save_on_supplier: e.target.checked })}
                  className="mt-0.5"
                />
                <span>
                  Enregistrer cette adresse sur la fiche du fournisseur (pour les prochains envois)
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingEmail(null)} disabled={missingEmail?.submitting}>Annuler</Button>
            <Button onClick={submitMissingEmail} disabled={missingEmail?.submitting} data-testid="missing-email-confirm-btn">
              <Send size={12} className="mr-1.5" /> {missingEmail?.submitting ? "..." : "Envoyer le BC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Iter-207n — Sub-component : BC number input with debounced uniqueness check
// against /outgoing-pos/check-number. Shown only in edit-mode for DRAFT/ISSUED.
function NumberFieldWithCheck({ poId, value, status, onChange }) {
  const [checkState, setCheckState] = React.useState({
    loading: false, available: null, collidingWith: null,
  });

  // Debounce 400ms after each keystroke before hitting the API. Skip on empty.
  React.useEffect(() => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
      setCheckState({ loading: false, available: null, collidingWith: null });
      return undefined;
    }
    setCheckState((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/outgoing-pos/check-number`, {
          params: { n: trimmed, exclude_po_id: poId },
        });
        setCheckState({
          loading: false,
          available: r.data?.available === true,
          collidingWith: r.data?.available === false ? `${r.data.supplier_name || "?"} (${r.data.status || "?"})` : null,
        });
      } catch {
        // Network/server hiccup → treat as unknown, don't block user.
        setCheckState({ loading: false, available: null, collidingWith: null });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [value, poId]);

  let indicator = null;
  if (checkState.loading) {
    indicator = (
      <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500" data-testid="po-number-check-loading">
        <Loader2 size={11} className="animate-spin" /> Vérification…
      </span>
    );
  } else if (checkState.available === true) {
    indicator = (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid="po-number-check-available">
        <Check size={11} /> Numéro disponible
      </span>
    );
  } else if (checkState.available === false) {
    indicator = (
      <span className="inline-flex items-center gap-1 text-[11px] text-red-700" data-testid="po-number-check-taken">
        <X size={11} /> Déjà utilisé par {checkState.collidingWith}
      </span>
    );
  }

  return (
    <div data-testid="po-form-number-block">
      <Label className="flex items-center gap-2">
        Numéro du BC
        {status === "DRAFT" && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded">
            Brouillon — auto-attribué si laissé vide
          </span>
        )}
      </Label>
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ex : BC-2026-007 ou un format personnalisé"
        data-testid="po-form-number-input"
        maxLength={60}
      />
      <div className="flex items-center justify-between mt-1 gap-3">
        <p className="text-[11px] text-zinc-500">
          {status === "ISSUED"
            ? "Modifiable tant que le BC n'a pas été envoyé. Le PDF Drive sera régénéré automatiquement."
            : "Si laissé vide, un numéro séquentiel BC-{année}-NNN sera attribué à l'émission."}
        </p>
        {indicator}
      </div>
    </div>
  );
}
